import type { PreferenceIssue } from './types';

/**
 * Console reporting for resolution issues, kept out of the resolver.
 *
 * `resolvePreferences` returns its issues rather than logging them, which keeps it pure and lets
 * tests assert on data instead of spying on the console. This is the other half: the application
 * calls it once per resolution so a preference the frontend could not honour is visible in a
 * browser console and in whatever collects them.
 *
 * Deduplicated, because preferences resolve on every profile change and an unrecognised code is a
 * standing condition rather than an event — without this it would print on every render pass that
 * touches the provider.
 */

const reported = new Set<string>();

const keyOf = (issue: PreferenceIssue): string =>
  `${issue.field}|${issue.received ?? ''}|${issue.reason}|${issue.resolvedTo}`;

/**
 * Logs each distinct issue once for the lifetime of the page.
 *
 * @param issues - Issues from a single `resolvePreferences` call.
 */
export const reportPreferenceIssues = (
  issues: readonly PreferenceIssue[]
): void => {
  for (const issue of issues) {
    const key = keyOf(issue);
    if (reported.has(key)) continue;

    reported.add(key);
    console.warn(
      `[localization] ${issue.reason} at ${issue.field}: received ${
        issue.received ?? '(nothing)'
      }, using ${issue.resolvedTo}`
    );
  }
};

/** Clears the dedupe memory. Exists for tests, which must not leak state between cases. */
export const resetPreferenceIssueReporting = (): void => {
  reported.clear();
};
